import type { ReactNode } from 'react';

// Reusable "tap me to send this prompt" chip. Used in two places:
//   1. Empty chat state — age-tuned starter prompts
//   2. After-answer follow-ups — contextual next questions
//
// Visually a soft pill with optional emoji prefix. Mobile-first sizing.
// Single source of truth so both surfaces stay consistent if we tune the look.

interface PromptChipProps {
  label: string;
  emoji?: string;
  onClick: () => void;
  disabled?: boolean;
  // 'block' = full width, 2-line, used in empty state
  // 'inline' = pill, single line, used in follow-ups
  variant?: 'block' | 'inline';
  description?: string; // only used in 'block' variant
  children?: ReactNode;
}

export function PromptChip({
  label,
  emoji,
  onClick,
  disabled,
  variant = 'inline',
  description,
}: PromptChipProps) {
  if (variant === 'block') {
    return (
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className="text-left rounded-xl border border-primary-100 bg-white px-4 py-3 hover:border-primary-300 hover:bg-primary-50/40 active:bg-primary-50 transition-colors disabled:opacity-50"
      >
        <div className="text-sm font-medium text-slate flex items-center gap-1.5">
          {emoji && <span aria-hidden>{emoji}</span>}
          <span>{label}</span>
        </div>
        {description && (
          <div className="text-xs text-slate-muted mt-0.5 leading-snug">{description}</div>
        )}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="inline-flex items-center gap-1.5 rounded-full bg-primary-50/80 hover:bg-primary-100 active:bg-primary-100 border border-primary-100 text-slate text-[13px] px-3 py-1.5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {emoji && <span aria-hidden className="text-sm">{emoji}</span>}
      <span className="leading-none">{label}</span>
    </button>
  );
}
